import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { gqlClient } from '@/api/graphql-client';
import meKeys from '@/api/queries/me/keyFactory';
import { graphql } from '@/gql';
import {
  CreateTeamInput,
  CreateTeamMutation,
  MyAccountTeamsQuery,
} from '@/gql/graphql';

const CREATE_TEAM_MUTATION = graphql(`
  mutation CreateTeam($input: CreateTeamInput!) {
    createTeam(input: $input) {
      id
      name
    }
  }
`);

const useCreateTeam = (
  options?: UseMutationOptions<CreateTeamMutation, unknown, CreateTeamInput>
) => {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options ?? {};

  return useMutation({
    mutationFn: (input) => {
      return gqlClient.request(CREATE_TEAM_MUTATION, { input });
    },
    onSuccess: (data, variables, context) => {
      const newTeam = data.createTeam;

      // Seed the new team into the cache so callers can select it immediately,
      // before the invalidated query refetches. Otherwise the Teams list's
      // "clear stale selection" guard would drop the selection in the gap.
      queryClient.setQueryData<MyAccountTeamsQuery>(
        meKeys.accountTeams(),
        (previous) => {
          if (!previous) {
            return previous;
          }
          const { teams } = previous.me.user.account;
          if (teams.items.some((team) => team.id === newTeam.id)) {
            return previous;
          }
          return {
            ...previous,
            me: {
              ...previous.me,
              user: {
                ...previous.me.user,
                account: {
                  ...previous.me.user.account,
                  teams: {
                    ...teams,
                    items: [
                      ...teams.items,
                      { id: newTeam.id, name: newTeam.name },
                    ],
                  },
                },
              },
            },
          };
        }
      );

      void queryClient.invalidateQueries({
        queryKey: meKeys.accountTeams(),
      });
      onSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
};

export default useCreateTeam;
